import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Autocomplete,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Business as ProjectIcon,
  Apartment as BuildingIcon,
  Layers as FloorIcon,
} from '@mui/icons-material';
import { getProjects, getPlotsByProject, createFloor, updateFloor, searchFloorData, createFlat } from '../../../services/project';
import { showSnackbar } from '../../../components/Snackbar';

const FloorForm = () => {
  const history = useHistory();
  const location = useLocation();
  const floorData = location.state?.floor;
  const buildingData = location.state?.building;
  const isEditMode = !!floorData;

  const urlParams = new URLSearchParams(location.search);
  const urlProjectId = urlParams.get('projectId');
  const urlBuildingId = urlParams.get('buildingId');

  const isPreSelectedProject = !!(buildingData?.projectId || urlProjectId);
  const isPreSelectedBuilding = !!(buildingData?.id || urlBuildingId);


  const [projects, setProjects] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [error, setError] = useState('');

  const initialFloorNo = (() => {
    const raw = floorData?.floorNo || floorData?.name || "";
    if (typeof raw === "number") return String(raw);
    const match = String(raw).match(/\d+/);
    if (match) return match[0];
    if (String(raw).toLowerCase().includes("ground")) return "0";
    return "";
  })();

  const [formData, setFormData] = useState({
    id: floorData?.id || floorData?._id || '',
    projectId: floorData?.projectId || buildingData?.projectId || urlProjectId || '',
    projectType: floorData?.projectType || buildingData?.projectType || 'Apartment',
    buildingId: floorData?.buildingId || buildingData?.id || urlBuildingId || '',
    floorNo: initialFloorNo,
    name: floorData?.name || floorData?.floorNo || '',
    totalFlats: floorData?.totalFlats || floorData?.totalFlat || '',
    status: floorData?.status || 'Available',
    remark: floorData?.remark || '',
  });

  useEffect(() => {
    fetchProjects();
    if (formData.projectId) {
      fetchBuildings(formData.projectId);
    }
    if (formData.buildingId && !isEditMode) {
      fetchNextFloorNo(formData.buildingId);
    }
  }, []);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const data = await getProjects({ projectType: formData.projectType });
      const projectsList = data || [];
      setProjects(projectsList);

      // Auto-select if only one project exists
      if (projectsList.length === 1 && !formData.projectId) {
        const singleProject = projectsList[0];
        setFormData(prev => ({ ...prev, projectId: singleProject._id || singleProject.id }));
        fetchBuildings(singleProject._id || singleProject.id);
      }
    } catch (err) {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchBuildings = async (projectId) => {
    try {
      setLoadingBuildings(true);
      const response = await getPlotsByProject(projectId, formData.projectType, formData.projectType);
      const inventories = Array.isArray(response) ? response : (response?.inventories || []);
      const mapped = inventories.map(b => ({ id: b._id || b.id, name: b.buildingName || b.name }));
      setBuildings(mapped);

      // Auto-select if only one building exists
      if (mapped.length === 1 && !formData.buildingId) {
        setFormData(prev => ({ ...prev, buildingId: mapped[0].id }));
      }
    } catch (err) {
      setBuildings([]);
    } finally {
      setLoadingBuildings(false);
    }
  };
  const fetchNextFloorNo = async (buildingId) => {
    if (!buildingId || isEditMode || formData.floorNo !== "") return;
    try {
      const floors = await searchFloorData({ buildingId });
      if (Array.isArray(floors) && floors.length > 0) {
        // Find maximum floorNo
        const floorNums = floors.map(f => Number(f.floorNo)).filter(n => !isNaN(n));
        const maxFloor = floorNums.length > 0 ? Math.max(...floorNums) : -1;
        const nextFloor = maxFloor + 1;
        const floorName = nextFloor === 0 ? "Ground Floor" : `Floor ${nextFloor}`;
        setFormData(prev => ({
          ...prev,
          floorNo: nextFloor.toString(),
          name: floorName
        }));
      } else {
        // Default to Ground Floor (0)
        setFormData(prev => ({
          ...prev,
          floorNo: "0",
          name: "Ground Floor"
        }));
      }
    } catch (err) {
      console.error('Error fetching next floor number:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFloorNoChange = (e) => {
    const val = e.target.value;
    const floorName = val ? (parseInt(val, 10) === 0 ? "Ground Floor" : `Floor ${val}`) : "";
    setFormData((prev) => ({
      ...prev,
      floorNo: val,
      name: floorName
    }));
  };

  const handleProjectChange = (event, newValue) => {
    const projectId = newValue?._id || '';
    setFormData((prev) => ({
      ...prev,
      projectId,
      buildingId: '', // Reset building when project changes
    }));
    if (projectId) {
      fetchBuildings(projectId);
    } else {
      setBuildings([]);
    }
  };

  const handleBuildingChange = (event) => {
    const buildingId = event.target.value;
    setFormData((prev) => ({ ...prev, buildingId }));
    if (buildingId && !isEditMode) {
      fetchNextFloorNo(buildingId);
    }
  };

  const handleSubmit = async () => {
    if (!formData.projectId || !formData.buildingId || formData.floorNo === '' || !formData.totalFlats || !formData.status) {
      setError('Please fill in all required fields (Project, Building, Floor Number, Total Flats, Status)');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const building = buildings.find(b => b.id === formData.buildingId || b._id === formData.buildingId);
      const project = projects.find(p => p._id === formData.projectId || p.id === formData.projectId);

      const statusMap = {
        "Available": "available",
        "Under Construction": "underConstruction",
      };

      const payload = {
        buildingId: formData.buildingId,
        projectId: formData.projectId,
        buildingName: building ? building.name : "",
        projectName: project ? project.projectName || project.name : "",
        inventoryType: formData.projectType || 'Apartment',
        projectType: formData.projectType || 'Apartment',
        floorNo: formData.name || (Number.isFinite(Number(formData.floorNo)) ? (Number(formData.floorNo) === 0 ? "Ground Floor" : `Floor ${formData.floorNo}`) : formData.floorNo),
        totalFlat: Number(formData.totalFlats),
        status: statusMap[formData.status] || (formData.status || '').toLowerCase(),
        remark: formData.remark,
        updatedBy: "Admin",
      };

      if (isEditMode) {
        await updateFloor(formData.id, payload);
      } else {
        const floorRes = await createFloor(payload);

        // Generate flats if floor count > 0
        const floorId = floorRes?.data?._id || floorRes?.data?.id || floorRes?._id || floorRes?.id;
        const totalFlatsNum = Number(formData.totalFlats) || 0;
        if (floorId && totalFlatsNum > 0) {
          const flatPromises = [];
          for (let i = 1; i <= totalFlatsNum; i++) {
            const flatNo = Number(formData.floorNo) * 100 + i;
            flatPromises.push(
              createFlat({
                projectId: formData.projectId,
                buildingId: formData.buildingId,
                floorId: floorId,
                flatNumber: flatNo,
                flatType: "2 BHK", // Default placeholder
                facing: "East",   // Default placeholder
                status: "available",
                inventoryType: formData.projectType || 'Apartment',
                remark: "",
                price: 0,
                plcPercent: 0,
                companyDiscountPercent: 0,
                cost: 0,
                guidelineValue: 0
              })
            );
          }
          try {
            await Promise.all(flatPromises);
          } catch (err) {
            console.error("[FloorForm] Partial flat generation failure", err);
          }
        }
      }

      showSnackbar({
        type: 'success',
        message: isEditMode ? 'Floor updated successfully!' : 'Floor added successfully!'
      });
      history.push('/app/project/apartment/floors', {
        building: { id: formData.buildingId, type: formData.projectType, projectId: formData.projectId }
      });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => history.goBack()} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a203e', mb: 0.5 }}>
              {isEditMode ? 'Edit Floor' : 'Add New Floor'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditMode ? `Updating floor details for ${formData.name}` : 'Define a new floor level within a building'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        {/* Hierarchy Assignment Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ProjectIcon color="primary" /> Hierarchy Assignment
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={projects}
              getOptionLabel={(option) => option.projectName || option.name || ''}
              value={projects.find(p => p._id === formData.projectId) || null}
              onChange={handleProjectChange}
              disabled={isEditMode || isPreSelectedProject}
              loading={loadingProjects}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingProjects ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth required>
              <InputLabel>Building</InputLabel>
              <Select
                name="buildingId"
                value={formData.buildingId}
                onChange={handleBuildingChange}
                label="Building"
                disabled={isEditMode || isPreSelectedBuilding || !formData.projectId || loadingBuildings}
              >
                {buildings.length === 0 && !loadingBuildings && (
                  <MenuItem disabled>No buildings found for this project</MenuItem>
                )}
                {buildings.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Usage / Property Type</InputLabel>
              <Select
                name="projectType"
                value={formData.projectType}
                onChange={(e) => {
                  handleInputChange(e);
                  setFormData(prev => ({ ...prev, projectId: '', buildingId: '' }));
                  setBuildings([]);
                  setProjects([]);
                  fetchProjects();
                }}
                disabled={isEditMode || isPreSelectedProject}
                label="Usage / Property Type"
              >
                <MenuItem value="Apartment">Apartment</MenuItem>
                <MenuItem value="Commercial">Commercial</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Floor Information Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FloorIcon color="primary" /> Floor Level Details
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Floor Number"
              type="number"
              value={formData.floorNo}
              onChange={handleFloorNoChange}
              required
              helperText="0 for Ground Floor, 1 for Floor 1, etc."
              placeholder="e.g. 1"
              hidden
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Floor Display Name"
              value={formData.name}
              InputProps={{ readOnly: true }}
              variant="filled"
              helperText="Automatically generated"
              hidden
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Total Flats"
              name="totalFlats"
              type="number"
              value={formData.totalFlats}
              onChange={handleInputChange}
              required
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth required>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                label="Status"
              >
                <MenuItem value="Available">Available</MenuItem>
                <MenuItem value="Under Construction">Under Construction</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Remarks Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Additional Information
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="remark"
              label="Floor Remarks / Notes"
              value={formData.remark}
              onChange={handleInputChange}
              multiline
              rows={3}
              placeholder="Any special notes about this floor level (e.g. higher ceilings, special flooring)..."
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ mt: 5, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => history.goBack()}
            sx={{ px: 4, py: 1.5, borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              px: 6,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 8px 16px rgba(55, 125, 255, 0.24)',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Floor' : 'Save Floor'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default FloorForm;
